'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import BannerArt from '@/components/BannerArt';
import Footer from '@/components/Footer';
import ScrollRevealInit from '@/app/components/ScrollRevealInit';
import AppImage from '@/components/ui/AppImage';

export default function AboutUsClient() {
  const [videoPlaying, setVideoPlaying] = useState(false);

  const values = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
        </svg>
      ),
      title: 'Transparent',
      desc: 'Every ingredient, listed. Every batch, tested. No preservatives and no hiding behind vague labels. We tell you exactly what your family is eating.',
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 20A7 7 0 0 1 4 13c0-5 4-9 16-9 0 12-4 16-9 16z" /><path d="M4 21c2-6 5-9 9-11" />
        </svg>
      ),
      title: 'Mindful',
      desc: 'We buy directly from farmers and mills. No middlemen and no risk of quality dilution. We make it our purpose to ensure what reaches your kitchen is nothing less than best!',
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 8l-9-5-9 5v8l9 5 9-5z" /><path d="M3 8l9 5 9-5M12 13v8" />
        </svg>
      ),
      title: 'No Shortcuts',
      desc: "We process in controlled small batches to ensure the flavour and nutrition of every batch stays intact. Scaling is easy, but we'd rather get it right!",
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      ),
      title: 'Made for You',
      desc: 'Not everyone has an hour every morning to make a healthy and balanced meal. Kasturi is made for the real modern Indian kitchens – quick, easy and guilt-free.',
    },
  ];

  return (
    <main className="grain-overlay bg-background min-h-screen overflow-x-hidden">
      <Header />
      {/* Hero */}
      <section
        id="hero"
        className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #2A1015 0%, #3A1820 55%, #2A1015 100%)' }}>
        <BannerArt />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <span className="block text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--accent)' }}>
            Kasturi Food Products · Since 1995
          </span>
          <h1
            className="font-display font-[200] text-white leading-none mb-6"
            style={{ fontSize: 'clamp(3rem, 8vw, 6rem)', letterSpacing: '0.02em' }}>
            Kasturi tastes like home.
          </h1>
          <p className="text-white/60 text-lg max-w-xl leading-relaxed mb-8">
            Meet the mother behind the name.
          </p>
          <a
            href="#the-reveal"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-all duration-300 hover:opacity-90"
            style={{ background: 'var(--accent)', color: 'var(--accent-foreground)' }}>
            Meet Kasturi
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          </a>
        </div>
      </section>
      {/* The Reveal */}
      <section id="the-reveal" className="py-12 md:py-16" style={{ background: 'var(--background)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="reveal reveal-left order-2 lg:order-1">
              <span className="block text-[10px] uppercase tracking-[0.25em] mb-5" style={{ color: 'var(--accent-ink)' }}>
                It&apos;s the feeling of home
              </span>
              <h2
                className="font-display leading-none mb-8"
                style={{ fontSize: 'clamp(2.2rem, 5vw, 4rem)', fontWeight: 200, letterSpacing: '0.02em', color: 'var(--foreground)' }}>
                Kasturi is more<br />
                than a{' '}
                <span className="italic font-light" style={{ color: 'var(--accent-ink)' }}>name.</span>
              </h2>
              <div className="space-y-5">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Kasturi is inspired by a mother who had a recipe that would be carried on through generations — the recipe of a traditional breakfast that she made every morning for her family. Roasted goodness, ground with dedication and commitment towards wholesome goodness.
                </p>
                <p className="text-foreground text-base leading-relaxed font-semibold">
                  Not packed. Not branded.<br />
                  Made only with the love of a mother.
                </p>
                <p className="text-foreground text-base leading-relaxed font-semibold">
                  Roasted grains, that smell like childhood.<br />
                  Grounded flavours, that taste like home.
                </p>
              </div>
              <div className="mt-8 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground italic">
                  &ldquo;My favourite part was the smell of Chhatua that came from the kitchen at 7am.&rdquo;
                </p>
              </div>
            </div>
            <div className="reveal reveal-right order-1 lg:order-2">
              <div className="relative rounded-3xl overflow-hidden" style={{ background: 'var(--secondary)', aspectRatio: '4/3' }}>
                <AppImage
                  src="/assets/images/kasturi_hero_1_grains.png"
                  alt="Roasted grains, ground the way a mother would"
                  fill
                  className="object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Why We Built Kasturi */}
      <section className="py-12 md:py-16" style={{ background: 'var(--secondary)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="block text-[10px] uppercase tracking-[0.25em] mb-5 reveal" style={{ color: 'var(--accent-ink)' }}>
            Our Reason
          </span>
          <h2
            className="font-display font-[200] mb-8 reveal"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '0.02em', color: 'var(--foreground)' }}>
            Why We Built Kasturi
          </h2>
          <p className="text-base leading-relaxed mb-10 reveal" style={{ color: 'var(--muted-foreground)' }}>
            Her recipe lived only in her memory, and in mine. If we didn&apos;t do something with it, it would be lost in a generation. So I did the one thing that felt honest. I put her name on it. And I made sure it was made the way she would have wanted.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10 text-left">
            {[
              'Perfection that couldn’t be watered down.',
              'Proportions that couldn’t be tweaked.',
              'Ingredients and quality that couldn’t be quietly negotiated with.',
            ]?.map((line, i) => (
              <div
                key={line}
                className={`p-5 rounded-2xl reveal stagger-${i + 1}`}
                style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mb-3" style={{ color: 'var(--accent-ink)' }}>
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--foreground)' }}>{line}</p>
              </div>
            ))}
          </div>
          <p className="font-display italic reveal" style={{ fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)', color: 'var(--accent-ink)' }}>
            &ldquo;Kasturi, is not just a brand. It is a mother&apos;s love, packaged to perfection!&rdquo;
          </p>
        </div>
      </section>
      {/* Then / Now */}
      <section className="py-12 md:py-16" style={{ background: 'var(--background)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14 reveal">
            <span className="block text-[10px] uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--accent-ink)' }}>
              How We Protect Her Recipe
            </span>
            <h2
              className="font-display font-[200] mb-4"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '0.02em', color: 'var(--foreground)' }}>
              How do you protect a mother&apos;s recipe?
            </h2>
            <p className="font-display italic" style={{ fontSize: 'clamp(1.1rem, 2vw, 1.5rem)', color: 'var(--muted-foreground)' }}>
              You build a factory around it.<br />
              And you tell her stories through every spoon!
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="reveal reveal-left p-8 md:p-10 rounded-3xl" style={{ background: 'var(--secondary)' }}>
              <span className="block text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--accent-ink)' }}>
                Then
              </span>
              <p className="text-lg leading-relaxed" style={{ color: 'var(--foreground)' }}>
                A mother&apos;s recipe, kept alive only in memory — made by hand, one family at a time.
              </p>
            </div>
            <div className="reveal reveal-right p-8 md:p-10 rounded-3xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <span className="block text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--accent-ink)' }}>
                Now
              </span>
              <ul className="space-y-3">
                {['Fully mechanised, zero-touch process.', 'No preservatives.', '100% wholesome goodness.']?.map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0" style={{ color: 'var(--accent-ink)' }}>
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span className="text-lg leading-relaxed" style={{ color: 'var(--foreground)' }}>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="text-center mt-12 reveal">
            <a
              href="/products"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-all duration-300 hover:opacity-90"
              style={{ background: 'var(--accent)', color: 'var(--accent-foreground)' }}>
              Explore Our Products
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>
      {/* Values */}
      <section className="py-16 md:py-24" style={{ background: 'var(--secondary)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14 reveal">
            <span className="block text-[10px] uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--accent-ink)' }}>
              What We Stand For
            </span>
            <h2
              className="font-display font-[200]"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '0.02em', color: 'var(--foreground)' }}>
              Our Core Values
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values?.map((v, i) => (
              <div
                key={v?.title}
                className={`p-6 rounded-2xl reveal stagger-${i + 1}`}
                style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'var(--secondary)', color: 'var(--accent-ink)' }}>
                  {v?.icon}
                </div>
                <h3 className="font-display font-[200] text-lg mb-2" style={{ color: 'var(--foreground)' }}>{v?.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{v?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Brand Video */}
      <section className="py-12 md:py-16" style={{ background: 'var(--background)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="reveal mb-8 flex items-center justify-between">
            <div>
              <span className="block text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: 'var(--accent-ink)' }}>
                Watch
              </span>
              <h3 className="font-display text-xl md:text-2xl font-[200]" style={{ color: 'var(--foreground)' }}>
                Kasturi Chhatua — A Daily Essential for Your Busy Life
              </h3>
            </div>
            <a
              href="https://www.youtube.com/channel/UC5JnFdZjwrI3Rj2zrB6i0DA"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 text-xs font-semibold uppercase tracking-wider hover:opacity-70 transition-opacity"
              style={{ color: 'var(--accent-ink)' }}>
              Our YouTube Channel
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M17 7H7M17 7v10" />
              </svg>
            </a>
          </div>
          <div className="reveal relative w-full rounded-2xl overflow-hidden" style={{ aspectRatio: '16/9', background: 'var(--foreground)' }}>
            {!videoPlaying ? (
              <>
                <img
                  src="https://img.youtube.com/vi/IuC3eod0YZw/maxresdefault.jpg"
                  alt="Watch Kasturi Chhatua — A Daily Essential for Your Busy Life"
                  className="w-full h-full object-cover opacity-70" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(26,18,8,0.6) 0%, transparent 60%)' }} />
                <button
                  onClick={() => setVideoPlaying(true)}
                  className="absolute inset-0 flex items-center justify-center group"
                  aria-label="Play Kasturi Chhatua video">
                  <div
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                    style={{ background: 'var(--accent)' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white" className="ml-1">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  </div>
                </button>
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-white font-display text-lg md:text-2xl font-[200]">
                    Kasturi Chhatua — A Daily Essential for Your Busy Life
                  </p>
                  <p className="text-white/60 text-sm mt-1">kasturifoodproducts · YouTube</p>
                </div>
              </>
            ) : (
              <iframe
                src="https://www.youtube.com/embed/IuC3eod0YZw?autoplay=1&rel=0"
                title="Kasturi Chhatua — A Daily Essential for Your Busy Life"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0" />
            )}
          </div>
          <div className="mt-6 text-center">
            <a
              href="/products"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-all duration-300 hover:opacity-90"
              style={{ background: 'var(--accent)', color: 'var(--accent-foreground)' }}>
              Explore Our Products
            </a>
          </div>
        </div>
      </section>
      <Footer />
      <ScrollRevealInit />
    </main>
  );
}
